import { ChildrenProps, Column, Image, Row, useMediaQuery } from "@hex-insights/core";
import { useRouteMatch } from "@hex-insights/router";
import { Icon, StaticURLs } from "@hex-insights/verita-romania.shared";
import { reEnrollmentDataConsentPageInfo } from "../../DataConsentPage/pageinfo";
import { reEnrollmentGuardiansPageInfo } from "../../GuardiansPage/pageinfo";
import { reEnrollmentMealPlansPageInfo } from "../../MealPlansPage/pageinfo";
import { reEnrollmentPaymentPlansPageInfo } from "../../PaymentPlansPage/pageinfo";
import { reEnrollmentReviewPageInfo } from "../../ReviewPage/pageinfo";
import { reEnrollmentStudentHealthPageInfo } from "../../StudentHealthPage/pageinfo";
import { reEnrollmentSuccessPageInfo } from "../../SuccessPage/pageinfo";
import styles from "./styles.module.css";

type ApplicationStep =
	| "guardians"
	| "student-health"
	| "data-consent"
	| "meal-plans"
	| "payment-plans"
	| "review"
	| "success";

export function ReEnrollmentSideBar() {
	const currentStepNumber = useCurrentStepNumber();

	const isScreenWide = useMediaQuery("(min-width: 900px)");
	if (!isScreenWide) {
		return null;
	}

	return (
		<Column className={styles["side-bar"]}>
			<Image
				src={StaticURLs.Images.veritaWingsSymmetricSmall}
				width={57}
				sizing="width-scaled"
				style={{ marginBottom: "5.625rem" }}
			/>

			<div style={{ fontWeight: "bold", color: "#8796B3", marginBottom: "1rem" }}>Verita International School</div>

			<div
				style={{
					fontSize: "1.375rem",
					fontWeight: "bold",
					color: "var(--brand-colors--primary--dark)",
					marginBottom: "2.5rem",
				}}
			>
				2024-2025 Re-Enrollment
			</div>

			<Column className={styles["progress"]}>
				<ProgressStep stepNumber={1} currentStepNumber={currentStepNumber}>
					Contact Information
				</ProgressStep>
				<ProgressStepLine stepNumber={1.5} currentStepNumber={currentStepNumber} />
				<ProgressStep stepNumber={2} currentStepNumber={currentStepNumber}>
					Health Details
				</ProgressStep>
				<ProgressStepLine stepNumber={2.5} currentStepNumber={currentStepNumber} />
				<ProgressStep stepNumber={3} currentStepNumber={currentStepNumber}>
					Data Consent
				</ProgressStep>
				<ProgressStepLine stepNumber={3.5} currentStepNumber={currentStepNumber} />
				<ProgressStep stepNumber={4} currentStepNumber={currentStepNumber}>
					Meal Plans
				</ProgressStep>
				<ProgressStepLine stepNumber={4.5} currentStepNumber={currentStepNumber} />
				<ProgressStep stepNumber={5} currentStepNumber={currentStepNumber}>
					Payment Plans
				</ProgressStep>
				<ProgressStepLine stepNumber={5.5} currentStepNumber={currentStepNumber} />
				<ProgressStep stepNumber={6} currentStepNumber={currentStepNumber}>
					Submit for Review
				</ProgressStep>
			</Column>
		</Column>
	);
}

type ProgressStepProps = {
	stepNumber: number;
	currentStepNumber: number;
} & Partial<ChildrenProps>;

function ProgressStep({ stepNumber, currentStepNumber, children }: ProgressStepProps) {
	const status = getStepStatus(stepNumber, currentStepNumber);

	const iconContent = status === "past" ? <Icon.Check size={20} className="block" /> : stepNumber;

	return (
		<Row
			justify="spaced-start"
			align="center"
			horizontalSpacing="0.5rem"
			className={styles[`progress-step--${status}`]}
		>
			<div className={styles["progress-step__icon"]}>
				<div className={styles["progress-step__icon__content"]}>{iconContent}</div>
			</div>

			<div className={styles["progress-step__label"]}>{children}</div>
		</Row>
	);
}

type ProgressStepLineProps = {
	stepNumber: number;
	currentStepNumber: number;
};

function ProgressStepLine({ stepNumber, currentStepNumber }: ProgressStepLineProps) {
	const status = getStepStatus(stepNumber, currentStepNumber);

	return <div className={styles["progress-step-line"] + " " + styles[`progress-step-line--${status}`]}></div>;
}

function useCurrentStepNumber() {
	const isOnGuardians = useRouteMatch(reEnrollmentGuardiansPageInfo);
	const isOnStudentHealth = useRouteMatch(reEnrollmentStudentHealthPageInfo);
	const isOnDataConsent = useRouteMatch(reEnrollmentDataConsentPageInfo);
	const isOnMealPlans = useRouteMatch(reEnrollmentMealPlansPageInfo);
	const isOnPaymentPlans = useRouteMatch(reEnrollmentPaymentPlansPageInfo);
	const isOnReview = useRouteMatch(reEnrollmentReviewPageInfo);
	const isOnSuccess = useRouteMatch(reEnrollmentSuccessPageInfo);

	if (isOnGuardians) {
		return stepToNumber("guardians");
	}
	if (isOnStudentHealth) {
		return stepToNumber("student-health");
	}
	if (isOnDataConsent) {
		return stepToNumber("data-consent");
	}
	if (isOnMealPlans) {
		return stepToNumber("meal-plans");
	}
	if (isOnPaymentPlans) {
		return stepToNumber("payment-plans");
	}
	if (isOnReview) {
		return stepToNumber("review");
	}
	if (isOnSuccess) {
		return stepToNumber("success");
	}
	return 0;
}

function stepToNumber(step: ApplicationStep) {
	switch (step) {
		case "guardians":
			return 1;
		case "student-health":
			return 2;
		case "data-consent":
			return 3;
		case "meal-plans":
			return 4;
		case "payment-plans":
			return 5;
		case "review":
			return 6;
		case "success":
			return 7;
	}
}

function getStepStatus(stepNumber: number, currentStepNumber: number) {
	if (stepNumber < currentStepNumber) {
		return "past";
	} else if (stepNumber === currentStepNumber) {
		return "current";
	} else {
		return "future";
	}
}
